import { ConversationRemoteRepository } from './service/conversation-remote-repository';
import { MessageRemoteRepository } from './service/message-remote-repository';
import { PredefinedSoundPlayer } from './sound/predefined-sound-player';
import { derived, get, writable } from 'svelte/store';
import { Role } from './types';
import { SessionStore } from './service/session-store';
import { params, pathname } from 'svelte-hash-router';
import { ConversationStore } from './service/conversation-store';
import { MySessionStore } from './service/my-session-store';
import { Walker } from './common/walker';
import { applicationData } from './service/application-properties';
import { getAssignedActiveConversations, hasAnyRole, isArchived, isOfSession, isPending, nullIfUndefined, wasEnded, wasStarted } from './service/util';
import { PopupService } from './common/popup-service';
import { Fetcher } from './common/fetcher';
import { MessagesStore } from './service/message-store';
import { NewItemsStore } from './common/new-items-store';
import { TokenRemoteRepository } from './service/token-remote-repository';
import { RichSessionRemoteRepository } from './service/rich-session-remote-repository';
import { UserRemoteRepository } from './service/user-remote-repository';
import { UserService } from './service/user-service';
import { DefinedMessageRemoteRepository } from './service/defined-message-remote-repository';
import { DefinedMessageService } from './service/defined-message-service';
import { StatePoller } from './service/state-poller';
import { StateRemoteRepository } from './service/state-remote-repository';
import { TokenSessionStorageRepository } from './service/token-session-storage-repository';
import { SessionRemoteRepository } from './service/session-remote-repository';
import { SoundModeAwareSoundPlayer } from './sound/sound-mode-aware-sound-player';
import { storageRepositoryStore } from './common/storage-repository-store';
import { StorageRepository } from './common/storage-repository';
import { SoundPlayerState, SoundsMode } from './sound/sound-types';
import { SessionService } from './service/session-service';
import { SoundPlayerStateAwareSoundPlayer } from './sound/sound-player-state-aware-sound-player';
import registerNewMessageNotificationSounds from './config/sound-notifications-registrar';
import { locale, locales } from 'svelte-i18n';
import { Paths } from './common/paths';
import { ContextService } from './service/context-service';
import { LocalizationService } from './service/localization-service';
import { TranslationRemoteRepository } from './service/translation-remote-repository';
import { UiConfigStaticRepository } from './service/ui-config-static-repository';
export const application = applicationData;
export const walker = new Walker();
export const showableLocales = derived(locales, (_locales) => {
    return _locales.filter(loc => loc !== LocalizationService.XX_LOCALE);
});
export const paths = derived(locale, _locale => new Paths(_locale));
export const popupService = new PopupService();
export const tokenSessionStorageRepository = new TokenSessionStorageRepository();
export const fetcher = new Fetcher(application.backendUrl, popupService, tokenSessionStorageRepository);
export const uiConfigRepository = new UiConfigStaticRepository(application.contexts);
export const contextService = new ContextService(uiConfigRepository);
export const translationRemoteRepository = new TranslationRemoteRepository(fetcher, contextService.getContextId());
export const localizationService = new LocalizationService(pathname, translationRemoteRepository, uiConfigRepository.getUiConfig());
export const soundsMode = storageRepositoryStore(new StorageRepository(window.localStorage, 'sounds_mode'), SoundsMode.ALL);
export const soundPlayerState = writable(SoundPlayerState.INITIAL);
export const anyUserInteractionPerformed = writable(false);
document.addEventListener('touchstart', () => anyUserInteractionPerformed.set(true), false);
document.addEventListener('click', () => anyUserInteractionPerformed.set(true), false);
anyUserInteractionPerformed.subscribe(value => {
    if (value) {
        soundPlayer.preload();
    }
});
const predefinedSoundPlayer = new PredefinedSoundPlayer(soundPlayerState);
const soundModeAwareSoundPlayer = new SoundModeAwareSoundPlayer(soundsMode, predefinedSoundPlayer);
export const soundPlayer = new SoundPlayerStateAwareSoundPlayer(soundPlayerState, soundModeAwareSoundPlayer);
export const currentConversationId = derived(params, $ => $.conversationId || null);
export const conversationRemoteRepository = new ConversationRemoteRepository(fetcher, contextService.getContextId());
export const messageRemoteRepository = new MessageRemoteRepository(fetcher);
export const tokenRemoteRepository = new TokenRemoteRepository(fetcher, contextService.getContextId(), tokenSessionStorageRepository);
export const sessionRemoteRepository = new SessionRemoteRepository(fetcher, contextService.getContextId());
export const sessions = new SessionStore(sessionRemoteRepository);
export const messages = new MessagesStore(messageRemoteRepository);
export const conversations = new ConversationStore(conversationRemoteRepository, message => messages.addMessage(message));
export const mySession = new MySessionStore(walker, paths, locale, sessionRemoteRepository, tokenSessionStorageRepository, tokenRemoteRepository, conversations);
export const richSessionRemoteRepository = new RichSessionRemoteRepository(fetcher, contextService.getContextId());
export const sessionService = new SessionService(richSessionRemoteRepository);
export const userRemoteRepository = new UserRemoteRepository(fetcher, contextService.getContextId());
export const userService = new UserService(userRemoteRepository);
export const definedMessageRemoteRepository = new DefinedMessageRemoteRepository(fetcher, contextService.getContextId());
export const definedMessageService = new DefinedMessageService(definedMessageRemoteRepository, contextService.getContextId());
export const stateRemoteRepository = new StateRemoteRepository(fetcher, contextService.getContextId());
export const statePoller = new StatePoller(stateRemoteRepository, polledConversations => conversations.update(polledConversations), polledMessages => messages.update(polledMessages));
export const activeOperatorsAndIntervisors = derived(sessions, (_sessions) => {
    return _sessions.filter(session => hasAnyRole(session, Role.OPERATOR, Role.INTERVISOR));
});
export const activeOperators = derived(sessions, (_sessions) => {
    return _sessions.filter(session => session.roles.includes(Role.OPERATOR));
});
export const currentConversation = derived([currentConversationId, conversations], ([_currentConversationId, _conversations]) => {
    if (!_currentConversationId) {
        return null;
    }
    return nullIfUndefined(_conversations.find(conv => conv.id === _currentConversationId));
});
export const activeConversations = derived(conversations, (_conversations) => {
    return _conversations
        .filter(conv => wasStarted(conv))
        .filter(conv => !wasEnded(conv));
});
export const pendingConversations = derived(conversations, (_conversations) => {
    return _conversations.filter(conv => isPending(conv));
});
export const myNotFinishedConversation = derived([mySession, conversations], ([_mySession, _conversations]) => {
    if (_mySession == null) {
        return null;
    }
    const myNotFinishedConversations = _conversations
        .filter(conv => isOfSession(conv, _mySession))
        .filter(conv => !wasEnded(conv));
    return myNotFinishedConversations.length === 0 ? null : myNotFinishedConversations[0];
});
export const myActiveConversation = derived([mySession, activeConversations], ([_mySession, _activeConversations]) => {
    const myActiveConversations = getAssignedActiveConversations(_mySession, _activeConversations);
    return myActiveConversations.length === 0 ? null : myActiveConversations[0];
});
export const myPendingConversations = derived([mySession, pendingConversations], ([_mySession, _pendingConversations]) => {
    if (_mySession == null || !(_mySession.roles.includes(Role.OPERATOR) || _mySession.roles.includes(Role.INTERVISOR))) {
        return [];
    }
    return _pendingConversations;
});
export const newConversations = new NewItemsStore(myPendingConversations, () => soundPlayer.playNewConversationNotificationLoop(), () => soundPlayer.stopNewConversationNotificationLoop());
export const endedConversations = derived(conversations, (_conversations) => {
    return _conversations.filter(conv => wasEnded(conv));
});
export const endedNotArchivedConversations = derived(endedConversations, (_conversations) => {
    return _conversations.filter(conv => !isArchived(conv));
});
localizationService.configure();
fetcher.addUnauthorizedExceptionListener(async () => {
    await mySession.logout();
    await walker.goTo(get(paths).home);
});
currentConversationId.subscribe(_currentConversationId => {
    statePoller.pollIfStarted();
});
mySession.subscribe(_mySession => {
    if (_mySession.roles.includes(Role.ANONYMOUS)) {
        statePoller.stop();
    }
    else {
        statePoller.start();
    }
});
registerNewMessageNotificationSounds(messages, myActiveConversation, soundPlayer);
window.addEventListener("beforeunload", e => mySession.sendCloseBeacon());
